/**
 * Shader prewarm — compile GLSL programs during a loading screen, never mid-game.
 *
 * On WKWebView (the installed iOS app) the GPU / WebContent memory budget is
 * FIXED and much tighter than a browser tab — which is why the game runs fine in
 * iPhone Chrome but loses the WebGL context in the installed app. A GLSL compile
 * + link is a transient memory/GPU spike. When that spike happens while the
 * budget is already near full (a busy room: many dice, the wallet, labels), it
 * can tip the process over → iOS jettisons the GPU process → context lost →
 * black screen. The more players, the fuller the budget, the sooner it tips —
 * which is exactly the reported symptom.
 *
 * Phone Dice never hits this because its `cook()` compiles every shader
 * permutation ONCE at init, so nothing compiles during play. This is the same
 * idea: warm the programs up front, while the player is watching a loading
 * screen, so the first roll / wallet reveal / bonus open never stalls or spikes.
 *
 * Mechanism — `renderer.compile(object, camera, targetScene)`:
 *   - `object`      supplies the MATERIALS to compile (it is traversed for meshes).
 *   - `targetScene` supplies the LIGHTS that define the program's cache key, so
 *                   the warmed program is byte-for-byte the one used live.
 * The object does NOT need to be in the scene graph — nothing is drawn, so there
 * is no flash and no render side effect. Three.js dedupes programs by cache key,
 * so warming one die warms every later die that shares that material config.
 *
 * `compileAsync` is awaited so a caller can block the loading screen on it (the
 * whole point — do this while the player waits). It uses
 * KHR_parallel_shader_compile where available and resolves only once every
 * program reports ready; without the extension it resolves immediately (the
 * compile is synchronous). Best-effort: never throws. A failure just means the
 * first live render may stall — the pre-fix behaviour, never worse.
 */

import * as THREE from 'three';
import { SharedRenderer } from './SharedRenderer';

export async function prewarmShaders(
  object: THREE.Object3D,
  camera: THREE.Camera,
  targetScene: THREE.Scene,
): Promise<void> {
  try {
    const shared = SharedRenderer.instance;
    if (shared.isContextLost) return; // don't touch a dead context
    await shared.gl.compileAsync(object, camera, targetScene);
  } catch (err) {
    console.warn('[prewarmShaders] shader prewarm failed (non-fatal)', err);
  }
}
